import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import type { DemoGalleryItem, DemoStory } from "~/routes/resources.cara-demo";
import { useT } from "~/i18n/context";
import { DemoStoryPage } from "./DemoStoryPage";

const LazyKioskGallery = lazy(() =>
  import("./KioskGallery").then((m) => ({ default: m.KioskGallery })),
);

function timeAgo(iso: string, labels: { justNow: string; minutes: string; hours: string; days: string }): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return labels.justNow;
  if (mins < 60) return labels.minutes.replace("{n}", String(mins));
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return labels.hours.replace("{n}", String(hrs));
  return labels.days.replace("{n}", String(Math.floor(hrs / 24)));
}

export function DemoGallery({ onTryIt }: { onTryIt?: () => void }) {
  const { t } = useT();
  const g = t.demo.gallery;
  const [items, setItems] = useState<DemoGalleryItem[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeStory, setActiveStory] = useState<DemoStory | null>(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState<string | null>(null);
  const [kioskMode, setKioskMode] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const fetchPage = useCallback(async (cursor: number) => {
    const isInitial = cursor === 0;
    if (isInitial) setLoading(true); else setLoadingMore(true);
    try {
      const res = await fetch(`/resources/cara-demo?intent=gallery&cursor=${cursor}&limit=24`);
      if (!res.ok) return;
      const data = await res.json();
      const fetched = (data.items ?? []) as DemoGalleryItem[];
      setItems((prev) => isInitial ? fetched : [...prev, ...fetched]);
      setNextCursor(data.nextCursor ?? null);
      setTotal(data.total ?? 0);
    } catch {
      // network error
    } finally {
      if (isInitial) setLoading(false); else setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(0);
  }, [fetchPage]);

  useEffect(() => {
    const timer = setInterval(() => fetchPage(0), 15_000);
    return () => clearInterval(timer);
  }, [fetchPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || nextCursor === null) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingMore && nextCursor !== null) {
          fetchPage(nextCursor);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, fetchPage]);

  const openStory = useCallback(async (storyId: string) => {
    setStoryLoading(true);
    setStoryError(null);
    try {
      const res = await fetch(`/resources/cara-demo?intent=story&id=${encodeURIComponent(storyId)}`);
      if (!res.ok) {
        setStoryError(res.status === 404 ? "Story not found" : `Failed to load story (${res.status})`);
        return;
      }
      const data = await res.json();
      if (!data.story) {
        setStoryError("Story not found");
        return;
      }

      if (data.source === "db") {
        const s = data.story;
        const normalized: DemoStory = {
          id: s.id,
          sessionId: s.sessionId,
          creatorName: s.creatorName ?? "",
          prompt: s.title ?? "Untitled Design",
          description: s.description,
          heroDataUrl: s.heroImageUrl,
          sketchDataUrl: s.sketchImageUrl,
          artifacts: (s.artifacts ?? []).map((a: Record<string, unknown>) => ({
            type: a.type as string,
            imageUrl: a.imageUrl as string | undefined,
            data: a.data as Record<string, unknown> | undefined,
            createdAt: a.createdAt as string,
          })),
          conversation: (s.conversation ?? []).map((m: Record<string, unknown>) => ({
            role: m.role as string,
            content: m.content as string,
            imageUrls: m.imageUrls as string[] | undefined,
            createdAt: m.createdAt as string | undefined,
          })),
          storyTitle: s.storyTitle,
          storyText: s.storyText,
          storySections: s.storySections,
          layout: s.layout,
          generatedImages: s.generatedImages,
          storyStatus: s.storyStatus ?? "ready",
          createdAt: s.createdAt,
        };
        setActiveStory(normalized);
      } else {
        setActiveStory(data.story as DemoStory);
      }
    } catch {
      setStoryError("Network error — please try again");
    } finally {
      setStoryLoading(false);
    }
  }, []);

  if (kioskMode) {
    return (
      <Suspense fallback={<div className="fixed inset-0 z-[100] bg-[#0a0908]" />}>
        <LazyKioskGallery onExit={() => setKioskMode(false)} />
      </Suspense>
    );
  }

  if (storyLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground" />
          <p className="text-sm text-muted-foreground">Loading story...</p>
        </div>
      </div>
    );
  }

  if (storyError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-destructive">{storyError}</p>
          <button
            onClick={() => { setStoryError(null); }}
            className="text-sm text-muted-foreground underline hover:text-foreground"
          >
            Back to gallery
          </button>
        </div>
      </div>
    );
  }

  if (activeStory) {
    return (
      <div className="h-full overflow-y-auto">
        <DemoStoryPage
          story={activeStory}
          onBack={() => setActiveStory(null)}
          onTryIt={onTryIt}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center">
        <span className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin text-muted-foreground/30 mb-4" />
        <p className="text-sm text-muted-foreground/50">{g.loading}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center">
        <div className="w-16 h-16 rounded-2xl border border-dashed border-border/50 flex items-center justify-center mb-5">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-7 w-7 text-muted-foreground/25">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
          </svg>
        </div>
        <p className="text-base font-display text-foreground/70 mb-2">{g.emptyTitle}</p>
        <p className="text-sm text-muted-foreground/50 max-w-xs leading-relaxed mb-6">
          {g.emptyBody}
        </p>
        {onTryIt && (
          <button
            onClick={onTryIt}
            className="text-[11px] uppercase tracking-[0.2em] font-medium px-6 py-2.5 transition-all duration-200 hover:brightness-110"
            style={{ background: "var(--teal-sapphire)", color: "var(--teal-sapphire-fg, #fff)" }}
          >
            {g.emptyAction}
          </button>
        )}
      </div>
    );
  }

  const countLabel = total === 1
    ? g.countSingular.replace("{count}", String(total))
    : g.countPlural.replace("{count}", String(total));

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-3 sm:px-5 lg:px-8 py-3 sm:py-4 border-b border-border/40">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-display text-foreground">{g.sectionTitle}</p>
            <p className="text-[11px] text-muted-foreground/50 mt-0.5">{countLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {items.length >= 2 && (
              <button
                onClick={() => setKioskMode(true)}
                className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-medium px-3 py-2 rounded-sm border border-border/40 text-muted-foreground/60 hover:text-foreground hover:border-border transition-all duration-200"
                title="Enter kiosk mode"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-3 w-3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                </svg>
                <span className="hidden sm:inline">Kiosk</span>
              </button>
            )}
            {onTryIt && (
              <button
                onClick={onTryIt}
                className="text-[10px] uppercase tracking-[0.18em] font-medium px-4 py-2 transition-all duration-200 hover:brightness-110"
                style={{ background: "var(--teal-sapphire)", color: "var(--teal-sapphire-fg, #fff)" }}
              >
                {g.createYours}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-5">
        <div className="columns-2 sm:columns-3 lg:columns-3 xl:columns-4 gap-3 [column-fill:_balance]">
          {items.map((item) => (
            <GalleryCard key={item.id} item={item} onOpenStory={openStory} />
          ))}
        </div>

        {nextCursor !== null && (
          <div ref={sentinelRef} className="flex justify-center py-6">
            {loadingMore && (
              <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin text-muted-foreground/30" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function GalleryCard({ item, onOpenStory }: { item: DemoGalleryItem; onOpenStory: (storyId: string) => void }) {
  const { t } = useT();
  const g = t.demo.gallery;
  const toolLabel = (g.toolLabels as Record<string, string>)[item.toolName] ?? g.fallbackTool;
  const storyId = item.storyId ?? item.id;

  return (
    <button
      onClick={() => onOpenStory(storyId)}
      className="group mb-3 break-inside-avoid w-full text-left rounded-xl border border-border/30 overflow-hidden transition-all duration-200 cursor-pointer hover:border-border/60 hover:shadow-md hover:shadow-black/[0.03] focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="relative">
        <img
          src={item.dataUrl}
          alt={item.prompt}
          className="w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <span
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] uppercase tracking-[0.12em] font-medium backdrop-blur-sm"
            style={{ background: "oklch(0.45 0.08 213 / 0.85)", color: "#fff" }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-2.5 w-2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
            {g.viewStory}
          </span>
        </div>
      </div>
      <div className="px-3 py-2.5">
        <p className="text-[11px] text-foreground/80 leading-relaxed line-clamp-2">{item.prompt}</p>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground/40 font-medium">
              {toolLabel}
            </span>
            {item.creatorName && (
              <>
                <span className="h-2.5 w-px bg-border/30 shrink-0" />
                <span className="text-[9px] text-muted-foreground/50 truncate">{item.creatorName}</span>
              </>
            )}
          </div>
          <span className="text-[9px] text-muted-foreground/30 shrink-0 ml-2">{timeAgo(item.createdAt, g.timeAgo)}</span>
        </div>
        <a
          href="/inspire?source=demo"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-1.5 inline-flex items-center gap-1 text-[8px] uppercase tracking-[0.14em] font-medium hover:underline"
          style={{ color: "var(--teal-sapphire)", opacity: 0.6 }}
        >
          View on Inspire
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-2 w-2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
          </svg>
        </a>
      </div>
    </button>
  );
}
